export type SdkErrorCategory =
  | 'network'
  | 'auth'
  | 'validation'
  | 'server'
  | 'realtime-degraded'
  | 'schema'
  | 'rate-limited'
  | 'precondition_failed'; // file 05 §2198 — wrapper-emitted upstream-error guard

export type NetworkCode = 'OFFLINE' | 'TIMEOUT' | 'TLS_FAIL' | 'FETCH_FAILED' | 'WSS_OPEN_FAILED';
export type AuthCode =
  | 'JWT_EXPIRED'
  | 'JWT_INVALID'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NATS_AUTH_REJECTED';
export type ValidationCode = 'BAD_REQUEST' | 'UNPROCESSABLE' | 'ZOD_PARSE_FAILED';
export type ServerCode = 'INTERNAL' | 'BAD_GATEWAY' | 'SERVICE_UNAVAILABLE' | 'NATS_NO_RESPONDERS';
export type RealtimeCode = 'NATS_DISCONNECTED' | 'SLOW_CONSUMER' | 'SUBJECT_REJECTED';
export type SchemaCode =
  | 'PROTO_DECODE_FAILED'
  | 'JSON_PARSE_FAILED'
  | 'SHAPE_MISMATCH'
  | 'UNKNOWN_ENUM';
export type RateCode = 'TOO_MANY_REQUESTS' | 'NATS_SLOW_CONSUMER';
export type PreconditionCode = 'UPSTREAM_ERROR' | 'STALE_OPTIMISTIC';

/** Fields present on every variant. */
export interface SdkErrorBase {
  readonly name: 'SdkError';
  readonly category: SdkErrorCategory;
  readonly code: string; // narrowed per variant below
  readonly userMessage: string; // i18n key, e.g. 'errors.network.offline'
  readonly devMessage: string; // English, for logs/devtool only
  readonly correlationId: string; // ULID; propagated from x-correlation-id
  readonly cause?: unknown; // chain origin (preserved across Error.cause)
  readonly emittedAt: number; // performance.now() at construction
}

export type SdkError =
  | (SdkErrorBase & { category: 'network'; code: NetworkCode })
  | (SdkErrorBase & { category: 'auth'; code: AuthCode; httpStatus: 401 | 403 })
  | (SdkErrorBase & {
      category: 'validation';
      code: ValidationCode;
      httpStatus: 400 | 422;
      fieldErrors: Readonly<Record<string, string>>;
    })
  | (SdkErrorBase & { category: 'server'; code: ServerCode; httpStatus: number })
  | (SdkErrorBase & {
      category: 'realtime-degraded';
      code: RealtimeCode;
      subjects: readonly string[];
    })
  | (SdkErrorBase & {
      category: 'schema';
      code: SchemaCode;
      subject?: string;
      path?: string;
    })
  | (SdkErrorBase & {
      category: 'rate-limited';
      code: RateCode;
      httpStatus: 429;
      retryAfterMs: number;
    })
  | (SdkErrorBase & { category: 'precondition_failed'; code: PreconditionCode });

/** Categories that bubble to `<SdkErrorBoundary>` (file 05 §2649-2655). */
export const THROW_CATEGORIES: ReadonlySet<SdkErrorCategory> = new Set(['schema']);

export function isSdkError(value: unknown): value is SdkError {
  return (
    typeof value === 'object' && value !== null && (value as { name?: unknown }).name === 'SdkError'
  );
}

export function shouldThrowToBoundary(err: SdkError): boolean {
  return THROW_CATEGORIES.has(err.category);
}
